import { getAuthToken } from '../../services/auth/tokenStorage';
import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  Alert,
  ActivityIndicator,
  ScrollView,
  Platform,
  TextInput,
  KeyboardAvoidingView,
} from 'react-native';
import { ms, s, vs } from '../../utils/responsive';
import { NavigationProp, useFocusEffect } from '@react-navigation/native';
import { Picker } from '@react-native-picker/picker';
import DateTimePickerModal from 'react-native-modal-datetime-picker';
import { launchImageLibrary, launchCamera, ImagePickerResponse, ImageLibraryOptions, CameraOptions } from 'react-native-image-picker';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { API_URL } from '../../config';

import logo from '../../assets/logo.png';
import backIcon from '../../assets/icons/back.png';

import {
  fetchAndOpenPrimaryMasterKey,
  hasCachedPrimaryMasterKey,
  TrustedPartyAccessDeniedError,
  TrustedPartyAccessNotReadyError,
} from '../../services/encryption/TrustedPartyAccess';

import { createLogger } from '../../utils/logger';
const logger = createLogger('RequestFullAccess');

interface RequestFullAccessScreenProps {
  navigation: NavigationProp<any>;
}

interface PrimaryUser {
  id: string;
  name: string;
  email: string;
}

// US States for the picker
const US_STATES = [
  { label: 'Select State', value: '' },
  { label: 'Alabama', value: 'AL' },
  { label: 'Alaska', value: 'AK' },
  { label: 'Arizona', value: 'AZ' },
  { label: 'Arkansas', value: 'AR' },
  { label: 'California', value: 'CA' },
  { label: 'Colorado', value: 'CO' },
  { label: 'Connecticut', value: 'CT' },
  { label: 'Delaware', value: 'DE' },
  { label: 'Florida', value: 'FL' },
  { label: 'Georgia', value: 'GA' },
  { label: 'Hawaii', value: 'HI' },
  { label: 'Idaho', value: 'ID' },
  { label: 'Illinois', value: 'IL' },
  { label: 'Indiana', value: 'IN' },
  { label: 'Iowa', value: 'IA' },
  { label: 'Kansas', value: 'KS' },
  { label: 'Kentucky', value: 'KY' },
  { label: 'Louisiana', value: 'LA' },
  { label: 'Maine', value: 'ME' },
  { label: 'Maryland', value: 'MD' },
  { label: 'Massachusetts', value: 'MA' },
  { label: 'Michigan', value: 'MI' },
  { label: 'Minnesota', value: 'MN' },
  { label: 'Mississippi', value: 'MS' },
  { label: 'Missouri', value: 'MO' },
  { label: 'Montana', value: 'MT' },
  { label: 'Nebraska', value: 'NE' },
  { label: 'Nevada', value: 'NV' },
  { label: 'New Hampshire', value: 'NH' },
  { label: 'New Jersey', value: 'NJ' },
  { label: 'New Mexico', value: 'NM' },
  { label: 'New York', value: 'NY' },
  { label: 'North Carolina', value: 'NC' },
  { label: 'North Dakota', value: 'ND' },
  { label: 'Ohio', value: 'OH' },
  { label: 'Oklahoma', value: 'OK' },
  { label: 'Oregon', value: 'OR' },
  { label: 'Pennsylvania', value: 'PA' },
  { label: 'Rhode Island', value: 'RI' },
  { label: 'South Carolina', value: 'SC' },
  { label: 'South Dakota', value: 'SD' },
  { label: 'Tennessee', value: 'TN' },
  { label: 'Texas', value: 'TX' },
  { label: 'Utah', value: 'UT' },
  { label: 'Vermont', value: 'VT' },
  { label: 'Virginia', value: 'VA' },
  { label: 'Washington', value: 'WA' },
  { label: 'West Virginia', value: 'WV' },
  { label: 'Wisconsin', value: 'WI' },
  { label: 'Wyoming', value: 'WY' },
];

export const RequestFullAccessScreen: React.FC<RequestFullAccessScreenProps> = ({ navigation }) => {
  const [primaryUsers, setPrimaryUsers] = useState<PrimaryUser[]>([]);
  const [selectedPrimaryUserId, setSelectedPrimaryUserId] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [approvedRequest, setApprovedRequest] = useState<any>(null);
  const [verificationStatus, setVerificationStatus] = useState<any>(null);

  // New verification fields
  const [deceasedFirstName, setDeceasedFirstName] = useState('');
  const [deceasedLastName, setDeceasedLastName] = useState('');
  const [deceasedDob, setDeceasedDob] = useState('');
  const [deceasedSsnLastFour, setDeceasedSsnLastFour] = useState('');
  const [deceasedDateOfDeath, setDeceasedDateOfDeath] = useState('');
  const [deceasedStateOfDeath, setDeceasedStateOfDeath] = useState('');
  const [dobPickerVisible, setDobPickerVisible] = useState(false);
  const [dateOfDeathPickerVisible, setDateOfDeathPickerVisible] = useState(false);

  useFocusEffect(
    React.useCallback(() => {
      loadPrimaryUsers();
    }, [])
  );

  useEffect(() => {
    if (selectedPrimaryUserId) {
      checkForApprovedRequest();
      // Pre-fill deceased name from primary user
      const selectedUser = primaryUsers.find(u => u.id === selectedPrimaryUserId);
      if (selectedUser) {
        const nameParts = selectedUser.name.split(' ');
        setDeceasedFirstName(nameParts[0] || '');
        setDeceasedLastName(nameParts.slice(1).join(' ') || '');
      }
    }
  }, [selectedPrimaryUserId, primaryUsers]);

  const loadPrimaryUsers = async () => {
    try {
      setLoading(true);
      const token = await getAuthToken();
      if (!token) return;

      const response = await fetch(`${API_URL}/api/relationships/primary-users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const result = await response.json();
        const successors = (result.primaryUsers || []).filter((u: any) => u.role === 'successor');
        setPrimaryUsers(successors);

        // Set first user as default if available
        if (successors.length > 0) {
          setSelectedPrimaryUserId(successors[0].id);
        }
      }
    } catch (error) {
      logger.error('Error loading primary users:', error);
      Alert.alert('Error', 'Failed to load primary users');
    } finally {
      setLoading(false);
    }
  };

  const checkForApprovedRequest = async () => {
    try {
      const token = await getAuthToken();
      if (!token || !selectedPrimaryUserId) return;

      const response = await fetch(
        `${API_URL}/api/death-certificate/approved-request/${selectedPrimaryUserId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.ok) {
        const result = await response.json();
        if (result.hasApprovedRequest && result.request) {
          setApprovedRequest(result.request);
          setVerificationStatus(result.request);
          // Medium-term #2 — once the death-cert + secret-code gate passes,
          // pre-warm the primary's master key into the session cache so any
          // downstream screen that reads their documents doesn't need to
          // wait on a fetch+unseal round-trip. Best effort — a failure
          // here surfaces when data is actually requested.
          if (
            result.request.secretCodeValidated &&
            !hasCachedPrimaryMasterKey(selectedPrimaryUserId)
          ) {
            primePrimaryMasterKey(selectedPrimaryUserId);
          }
        } else {
          setApprovedRequest(null);
          setVerificationStatus(null);
        }
      }
    } catch (error) {
      logger.error('Error checking for approved request:', error);
    }
  };

  const primePrimaryMasterKey = async (primaryUserId: string) => {
    try {
      await fetchAndOpenPrimaryMasterKey(primaryUserId);
      logger.info('Primary master key cached for successor access');
    } catch (err) {
      if (err instanceof TrustedPartyAccessNotReadyError) {
        // User needs to unlock their OWN vault before we can open the
        // sealed blob. Surface as an alert so they act on it.
        Alert.alert(
          'Unlock your vault',
          'Enter your Recovery Code so we can decrypt your loved one\'s data.',
        );
      } else if (err instanceof TrustedPartyAccessDeniedError) {
        // Backend says no sealed blob is available yet. This can happen
        // when the primary never completed the handshake for this
        // successor — log but don't alarm the user; existing
        // access-grant flows will still work for data the primary
        // uploaded via the legacy path.
        logger.warn(
          'No trusted-party wrapped key available yet; legacy access only',
        );
      } else {
        logger.error('Priming primary master key failed:', err);
      }
    }
  };

  const handleImageResponse = (response: ImagePickerResponse) => {
    if (response.didCancel) {
      logger.debug('User cancelled image picker');
      return;
    }

    if (response.errorCode) {
      logger.error('ImagePicker Error:', response.errorMessage);
      Alert.alert('Error', response.errorMessage || 'Failed to select image');
      return;
    }

    if (response.assets && response.assets.length > 0) {
      const selectedImage = response.assets[0];

      // Check file size (5MB limit)
      if (selectedImage.fileSize && selectedImage.fileSize > 5 * 1024 * 1024) {
        Alert.alert('Error', 'File size must be less than 5MB');
        return;
      }

      setSelectedFile({
        uri: selectedImage.uri,
        type: selectedImage.type || 'image/jpeg',
        name: selectedImage.fileName || 'death-certificate.jpg',
        size: selectedImage.fileSize || 0,
        // The web picker also hands over the File itself (the shim's
        // `file`); the upload uses it directly, see handleSubmit.
        file: (selectedImage as any).file,
      });
    }
  };

  const handleSelectFromGallery = async () => {
    const options: ImageLibraryOptions = {
      mediaType: 'photo',
      quality: 0.9,
      maxWidth: 2000,
      maxHeight: 2000,
      selectionLimit: 1,
    };

    try {
      const result = await launchImageLibrary(options);
      handleImageResponse(result);
    } catch (error) {
      logger.error('Gallery error:', error);
      Alert.alert('Error', 'Failed to open gallery. Please check photo library permissions.');
    }
  };

  const handleTakePhoto = async () => {
    const options: CameraOptions = {
      mediaType: 'photo',
      quality: 0.9,
      maxWidth: 2000,
      maxHeight: 2000,
      saveToPhotos: false,
    };

    try {
      const result = await launchCamera(options);
      handleImageResponse(result);
    } catch (error) {
      logger.error('Camera error:', error);
      Alert.alert('Error', 'Failed to open camera. Please check camera permissions.');
    }
  };

  const handleSelectFile = () => {
    Alert.alert(
      'Select Death Certificate',
      'Choose how to provide the death certificate image',
      [
        {
          text: 'Take Photo',
          onPress: handleTakePhoto,
        },
        {
          text: 'Choose from Gallery',
          onPress: handleSelectFromGallery,
        },
        {
          text: 'Cancel',
          style: 'cancel',
        },
      ]
    );
  };

  const validateForm = (): boolean => {
    if (!deceasedFirstName.trim()) {
      Alert.alert('Error', 'Please enter the deceased person\'s first name');
      return false;
    }
    if (!deceasedLastName.trim()) {
      Alert.alert('Error', 'Please enter the deceased person\'s last name');
      return false;
    }
    if (!deceasedDob.trim()) {
      Alert.alert('Error', 'Please enter the deceased person\'s date of birth');
      return false;
    }
    if (!isValidDisplayDate(deceasedDob)) {
      Alert.alert('Error', 'Please select a valid date of birth');
      return false;
    }
    // Validate SSN last 4 if provided
    if (deceasedSsnLastFour && !/^\d{4}$/.test(deceasedSsnLastFour)) {
      Alert.alert('Error', 'SSN last 4 digits must be exactly 4 numbers');
      return false;
    }
    if (deceasedDateOfDeath && !isValidDisplayDate(deceasedDateOfDeath)) {
      Alert.alert('Error', 'Please select a valid date of death');
      return false;
    }
    return true;
  };

  const formatDateForDisplay = (date: Date): string => {
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const parseDisplayDate = (dateStr: string): Date | undefined => {
    if (!dateStr) return undefined;
    const [month, day, year] = dateStr.split('/').map(Number);
    if (!month || !day || !year) return undefined;
    const parsed = new Date(year, month - 1, day);
    if (
      parsed.getFullYear() !== year ||
      parsed.getMonth() !== month - 1 ||
      parsed.getDate() !== day
    ) {
      return undefined;
    }
    return parsed;
  };

  const isValidDisplayDate = (dateStr: string): boolean => {
    return !!parseDisplayDate(dateStr);
  };

  const formatDateForApi = (dateStr: string): string => {
    // Convert MM/DD/YYYY to YYYY-MM-DD
    const parsed = parseDisplayDate(dateStr);
    if (!parsed) return '';
    const month = `${parsed.getMonth() + 1}`.padStart(2, '0');
    const day = `${parsed.getDate()}`.padStart(2, '0');
    return `${parsed.getFullYear()}-${month}-${day}`;
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      Alert.alert('Error', 'Please select a death certificate file');
      return;
    }

    if (!selectedPrimaryUserId) {
      Alert.alert('Error', 'Please select a primary user');
      return;
    }

    if (!validateForm()) {
      return;
    }

    try {
      setUploading(true);
      const token = await getAuthToken();
      if (!token) return;

      const formData = new FormData();
      if (Platform.OS === 'web') {
        // A browser's FormData needs the bytes, not the phone's {uri, type,
        // name} description: that object was sent as the text
        // "[object Object]" and the API refused the field (2026-10-22).
        // The picker keeps the File; use it. (Reading the blob: URL back
        // with fetch is blocked by the page's connect-src policy.)
        const file = selectedFile.file;
        if (!(file instanceof Blob)) {
          Alert.alert('Error', 'Please choose the certificate file again.');
          return;
        }
        formData.append('certificate', file as any);
      } else {
        formData.append('certificate', {
          uri: Platform.OS === 'ios' ? selectedFile.uri.replace('file://', '') : selectedFile.uri,
          type: selectedFile.type || 'image/jpeg',
          name: selectedFile.name || 'death-certificate.jpg',
        } as any);
      }
      formData.append('primaryUserId', selectedPrimaryUserId);
      formData.append('deceasedFirstName', deceasedFirstName.trim());
      formData.append('deceasedLastName', deceasedLastName.trim());
      formData.append('deceasedDob', formatDateForApi(deceasedDob));

      if (deceasedSsnLastFour) {
        formData.append('deceasedSsnLastFour', deceasedSsnLastFour);
      }
      if (deceasedDateOfDeath) {
        formData.append('deceasedDateOfDeath', formatDateForApi(deceasedDateOfDeath));
      }
      if (deceasedStateOfDeath) {
        formData.append('deceasedStateOfDeath', deceasedStateOfDeath);
      }

      // No Content-Type here: with a FormData body the platform sets
      // "multipart/form-data; boundary=…" itself. Setting it by hand left
      // the boundary out in the browser, and the API answered "Multipart:
      // Boundary not found" (web, 2026-10-22). The phone adds it either way.
      const response = await fetch(`${API_URL}/api/death-verification/initiate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      const result = await response.json();

      if (response.ok) {
        setVerificationStatus(result);
        Alert.alert(
          'Verification Started',
          'Your death certificate is being verified. This process includes AI analysis and database verification. You will be notified of the result.',
          [
            {
              text: 'OK',
              onPress: () => {
                setSelectedFile(null);
                checkForApprovedRequest();
              }
            }
          ]
        );
      } else {
        Alert.alert('Error', result.message || 'Failed to submit verification request');
      }
    } catch (error) {
      logger.error('Error submitting verification:', error);
      Alert.alert('Error', 'Failed to submit verification request');
    } finally {
      setUploading(false);
    }
  };

  const handleEnterSecretCode = () => {
    if (approvedRequest) {
      (navigation as any).navigate('SecretCode', {
        requestId: approvedRequest.id,
        primaryUserId: selectedPrimaryUserId,
      });
    }
  };

  const getSelectedUserName = () => {
    const user = primaryUsers.find(u => u.id === selectedPrimaryUserId);
    return user ? user.name : 'Select a user';
  };

  const getSelectedStateName = () => {
    const state = US_STATES.find(s => s.value === deceasedStateOfDeath);
    return state ? state.label : 'Select State';
  };

  const renderVerificationStatus = () => {
    if (!verificationStatus) return null;

    const { status, combinedConfidenceScore, thirdPartyVerified, verificationFactors } = verificationStatus;

    const getStatusColor = () => {
      if (status === 'approved' || status === 'secret_code_validated') return '#28A745';
      if (status === 'rejected') return '#DC3545';
      if (status === 'manual_review_pending') return '#FFC107';
      return '#17A2B8';
    };

    const getStatusText = () => {
      switch (status) {
        case 'pending': return 'Pending';
        case 'ai_analyzing': return 'Analyzing Document...';
        case 'manual_review_pending': return 'Under Review';
        case 'secret_code_pending': return 'Awaiting Secret Code';
        case 'secret_code_validated': return 'Verified';
        case 'approved': return 'Approved';
        case 'rejected': return 'Rejected';
        default: return status;
      }
    };

    return (
      <View style={styles.statusContainer}>
        <Text style={styles.statusTitle}>Verification Status</Text>
        <View style={[styles.statusBadge, { backgroundColor: getStatusColor() }]}>
          <Text style={styles.statusBadgeText}>{getStatusText()}</Text>
        </View>

        {combinedConfidenceScore !== null && (
          <View style={styles.scoreContainer}>
            <Text style={styles.scoreLabel}>Confidence Score</Text>
            <Text style={styles.scoreValue}>{combinedConfidenceScore}%</Text>
          </View>
        )}

        {verificationFactors && (
          <View style={styles.factorsContainer}>
            <Text style={styles.factorsTitle}>Verification Factors:</Text>
            <View style={styles.factorRow}>
              <Text style={styles.factorLabel}>Database Match:</Text>
              <Text style={[styles.factorValue, { color: thirdPartyVerified ? '#28A745' : '#DC3545' }]}>
                {thirdPartyVerified ? '✓ Verified' : '✗ Not Found'}
              </Text>
            </View>
            <View style={styles.factorRow}>
              <Text style={styles.factorLabel}>Name Consistency:</Text>
              <Text style={[styles.factorValue, { color: verificationFactors.nameConsistency ? '#28A745' : '#DC3545' }]}>
                {verificationFactors.nameConsistency ? '✓' : '✗'}
              </Text>
            </View>
            <View style={styles.factorRow}>
              <Text style={styles.factorLabel}>Date Consistency:</Text>
              <Text style={[styles.factorValue, { color: verificationFactors.dateConsistency ? '#28A745' : '#DC3545' }]}>
                {verificationFactors.dateConsistency ? '✓' : '✗'}
              </Text>
            </View>
            <View style={styles.factorRow}>
              <Text style={styles.factorLabel}>Document Authenticity:</Text>
              <Text style={[styles.factorValue, { color: verificationFactors.documentAuthenticity ? '#28A745' : '#DC3545' }]}>
                {verificationFactors.documentAuthenticity ? '✓' : '✗'}
              </Text>
            </View>
          </View>
        )}
      </View>
    );
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#1C2C4F" />
          <Text style={styles.loadingText}>Loading...</Text>
        </View>
      </SafeAreaView>
    );
  }

  if (primaryUsers.length === 0) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity style={styles.backButton} onPress={() => navigation.goBack()}>
            <Image source={backIcon} style={styles.backIcon} />
          </TouchableOpacity>
          <Image source={logo} style={styles.logo} resizeMode="contain" />
        </View>
        <View style={styles.emptyContainer}>
          <Text style={styles.emptyText}>You are not a successor to any users.</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={{ flex: 1 }}
      >
        <View style={styles.header}>
          <TouchableOpacity style={styles.backButton} onPress={() => navigation.goBack()}>
            <Image source={backIcon} style={styles.backIcon} />
          </TouchableOpacity>
          <Image source={logo} style={styles.logo} resizeMode="contain" />
        </View>

        <ScrollView style={styles.content} contentContainerStyle={styles.scrollContent}>
          <Text style={styles.title}>Request Full Access</Text>
          <Text style={styles.subtitle}>
            Submit a death verification request to access a primary user's data
          </Text>

          {/* Primary User Picker - Dashboard Style */}
          <View style={styles.section}>
            <Text style={styles.label}>Select Primary User</Text>
            <View style={styles.dashboardPickerContainer}>
              <Text style={styles.swipeIndicator}>▲</Text>
              <View style={styles.dashboardPickerWrapper}>
                <Picker
                  selectedValue={selectedPrimaryUserId}
                  onValueChange={(value) => setSelectedPrimaryUserId(value)}
                  mode={Platform.OS === 'ios' ? 'dropdown' : 'dialog'}
                  itemStyle={styles.pickerItem}
                >
                  {primaryUsers.map((user) => (
                    <Picker.Item key={user.id} label={user.name} value={user.id} />
                  ))}
                </Picker>
              </View>
              <Text style={styles.swipeIndicator}>▼</Text>
            </View>
          </View>

          {approvedRequest && !approvedRequest.secretCodeValidated && (
            <View style={styles.approvedContainer}>
              <Text style={styles.approvedTitle}>✓ Death Certificate Approved</Text>
              <Text style={styles.approvedText}>
                Your death certificate has been approved. A secret code has been sent to the designated recipients.
                Please enter it below to unlock full access.
              </Text>
              <TouchableOpacity
                style={styles.secretCodeButton}
                onPress={handleEnterSecretCode}
              >
                <Text style={styles.secretCodeButtonText}>Enter Secret Code</Text>
              </TouchableOpacity>
            </View>
          )}

          {approvedRequest && approvedRequest.secretCodeValidated && (
            <View style={styles.completedContainer}>
              <Text style={styles.completedTitle}>✓ Full Access Granted</Text>
              <Text style={styles.completedText}>
                You now have full access to this user's data.
              </Text>
            </View>
          )}

          {verificationStatus && !approvedRequest && renderVerificationStatus()}

          {!approvedRequest && !verificationStatus && (
            <>
              {/* Deceased Person Information */}
              <View style={styles.sectionHeader}>
                <Text style={styles.sectionHeaderText}>Deceased Person Information</Text>
                <Text style={styles.sectionSubtext}>
                  This information is used to verify the death against official records
                </Text>
              </View>

              <View style={styles.inputRow}>
                <View style={styles.inputHalf}>
                  <Text style={styles.inputLabel}>First Name *</Text>
                  <TextInput
                    style={styles.input}
                    value={deceasedFirstName}
                    onChangeText={setDeceasedFirstName}
                    placeholder="First name"
                    placeholderTextColor="#999"
                  />
                </View>
                <View style={styles.inputHalf}>
                  <Text style={styles.inputLabel}>Last Name *</Text>
                  <TextInput
                    style={styles.input}
                    value={deceasedLastName}
                    onChangeText={setDeceasedLastName}
                    placeholder="Last name"
                    placeholderTextColor="#999"
                  />
                </View>
              </View>

              <View style={styles.section}>
                <Text style={styles.inputLabel}>Date of Birth *</Text>
                <TouchableOpacity
                  style={styles.input}
                  onPress={() => setDobPickerVisible(true)}
                  accessibilityRole="button"
                  accessibilityLabel="Select date of birth"
                >
                  <Text style={deceasedDob ? styles.dateInputText : styles.dateInputPlaceholder}>
                    {deceasedDob || 'Select date of birth'}
                  </Text>
                </TouchableOpacity>
                <DateTimePickerModal
                  isVisible={dobPickerVisible}
                  mode="date"
                  date={parseDisplayDate(deceasedDob) || new Date(1970, 0, 1)}
                  display={Platform.OS === 'ios' ? 'spinner' : 'default'}
                  maximumDate={new Date()}
                  onConfirm={(date) => {
                    setDeceasedDob(formatDateForDisplay(date));
                    setDobPickerVisible(false);
                  }}
                  onCancel={() => setDobPickerVisible(false)}
                />
              </View>

              <View style={styles.section}>
                <Text style={styles.inputLabel}>SSN Last 4 Digits (Optional)</Text>
                <TextInput
                  style={styles.input}
                  value={deceasedSsnLastFour}
                  onChangeText={setDeceasedSsnLastFour}
                  placeholder="1234"
                  placeholderTextColor="#999"
                  keyboardType="numeric"
                  maxLength={4}
                  secureTextEntry
                />
                <Text style={styles.helperText}>
                  Providing SSN increases verification accuracy
                </Text>
              </View>

              <View style={styles.section}>
                <Text style={styles.inputLabel}>Date of Death (Optional)</Text>
                <TouchableOpacity
                  style={styles.input}
                  onPress={() => setDateOfDeathPickerVisible(true)}
                  accessibilityRole="button"
                  accessibilityLabel="Select date of death"
                >
                  <Text style={deceasedDateOfDeath ? styles.dateInputText : styles.dateInputPlaceholder}>
                    {deceasedDateOfDeath || 'Select date of death'}
                  </Text>
                </TouchableOpacity>
                <DateTimePickerModal
                  isVisible={dateOfDeathPickerVisible}
                  mode="date"
                  date={parseDisplayDate(deceasedDateOfDeath) || new Date()}
                  display={Platform.OS === 'ios' ? 'spinner' : 'default'}
                  maximumDate={new Date()}
                  onConfirm={(date) => {
                    setDeceasedDateOfDeath(formatDateForDisplay(date));
                    setDateOfDeathPickerVisible(false);
                  }}
                  onCancel={() => setDateOfDeathPickerVisible(false)}
                />
              </View>

              {/* State Picker - Dashboard Style */}
              <View style={styles.section}>
                <Text style={styles.inputLabel}>State of Death (Optional)</Text>
                <View style={styles.dashboardPickerContainer}>
                  <Text style={styles.swipeIndicator}>▲</Text>
                  <View style={styles.dashboardPickerWrapper}>
                    <Picker
                      selectedValue={deceasedStateOfDeath}
                      onValueChange={(value) => setDeceasedStateOfDeath(value)}
                      mode={Platform.OS === 'ios' ? 'dropdown' : 'dialog'}
                      itemStyle={styles.pickerItem}
                    >
                      {US_STATES.map((state) => (
                        <Picker.Item key={state.value} label={state.label} value={state.value} />
                      ))}
                    </Picker>
                  </View>
                  <Text style={styles.swipeIndicator}>▼</Text>
                </View>
              </View>

              {/* Death Certificate Upload */}
              <View style={styles.sectionHeader}>
                <Text style={styles.sectionHeaderText}>Death Certificate</Text>
              </View>

              <View style={styles.section}>
                <TouchableOpacity style={styles.fileButton} onPress={handleSelectFile}>
                  {selectedFile ? (
                    <View style={styles.selectedFileContainer}>
                      <Image
                        source={{ uri: selectedFile.uri }}
                        style={styles.thumbnailImage}
                        resizeMode="cover"
                      />
                      <View style={styles.fileDetails}>
                        <Text style={styles.fileNameText} numberOfLines={1}>
                          {selectedFile.name}
                        </Text>
                        <Text style={styles.fileSizeText}>
                          {(selectedFile.size / 1024).toFixed(1)} KB
                        </Text>
                      </View>
                      <Text style={styles.changeText}>Change</Text>
                    </View>
                  ) : (
                    <View style={styles.uploadPlaceholder}>
                      <Text style={styles.uploadIcon}>📷</Text>
                      <Text style={styles.fileButtonText}>
                        Tap to take photo or select from gallery
                      </Text>
                      <Text style={styles.fileHintText}>
                        JPG, PNG - Max 5MB
                      </Text>
                    </View>
                  )}
                </TouchableOpacity>
              </View>

              <TouchableOpacity
                style={[styles.uploadButton, (uploading || !selectedFile) && styles.uploadButtonDisabled]}
                onPress={handleUpload}
                disabled={uploading || !selectedFile}
              >
                {uploading ? (
                  <ActivityIndicator color="#FFFFFF" />
                ) : (
                  <Text style={styles.uploadButtonText}>Submit Verification Request</Text>
                )}
              </TouchableOpacity>

              <View style={styles.infoContainer}>
                <Text style={styles.infoTitle}>How Verification Works:</Text>
                <Text style={styles.infoText}>
                  1. AI analyzes your death certificate for authenticity
                </Text>
                <Text style={styles.infoText}>
                  2. Information is verified against official death records
                </Text>
                <Text style={styles.infoText}>
                  3. High-confidence matches may be auto-approved
                </Text>
                <Text style={styles.infoText}>
                  4. A secret code will be sent to designated recipients
                </Text>
                <Text style={styles.infoText}>
                  5. Enter the secret code to complete verification
                </Text>
              </View>
            </>
          )}
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F3F6F8',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(15),
    paddingTop: ms(10),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    top: '50%',
    transform: [{ translateY: -12 }],
    padding: ms(5),
    zIndex: 1,
  },
  backIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#333',
  },
  logo: {
    width: s(80),
    height: s(80),
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: ms(10),
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: ms(20),
  },
  emptyText: {
    fontSize: ms(16, 0.3),
    color: '#666',
    textAlign: 'center',
  },
  content: {
    flex: 1,
  },
  scrollContent: {
    padding: ms(20),
    paddingBottom: ms(40),
  },
  title: {
    fontSize: ms(22, 0.3),
    fontWeight: 'bold',
    color: '#1C2C4F',
    textAlign: 'center',
    marginBottom: ms(8),
  },
  subtitle: {
    fontSize: ms(14, 0.3),
    color: '#666',
    textAlign: 'center',
    marginBottom: ms(25),
  },
  sectionHeader: {
    marginTop: ms(10),
    marginBottom: ms(15),
  },
  sectionHeaderText: {
    fontSize: ms(16, 0.3),
    fontWeight: '700',
    color: '#1C2C4F',
    marginBottom: ms(4),
  },
  sectionSubtext: {
    fontSize: ms(12, 0.3),
    color: '#666',
  },
  section: {
    marginBottom: ms(18),
  },
  label: {
    fontSize: ms(14, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginBottom: ms(8),
  },
  inputLabel: {
    fontSize: ms(13, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginBottom: ms(6),
  },
  input: {
    backgroundColor: '#FFF',
    borderRadius: ms(10),
    borderWidth: ms(1),
    borderColor: '#DDD',
    padding: ms(12),
    fontSize: ms(15, 0.3),
    color: '#333',
    minHeight: vs(44),
    justifyContent: 'center',
  },
  dateInputText: {
    fontSize: ms(15, 0.3),
    color: '#333',
  },
  dateInputPlaceholder: {
    fontSize: ms(15, 0.3),
    color: '#999',
  },
  inputRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: ms(18),
  },
  inputHalf: {
    width: '48%',
  },
  helperText: {
    fontSize: ms(11, 0.3),
    color: '#888',
    marginTop: ms(4),
    fontStyle: 'italic',
  },
  // Dashboard-style picker
  dashboardPickerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    borderRadius: ms(12),
    paddingHorizontal: ms(10),
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  dashboardPickerWrapper: {
    flex: 1,
    backgroundColor: '#fff',
    borderRadius: ms(10),
    height: Platform.OS === 'ios' ? 120 : 50,
    justifyContent: 'center',
    overflow: 'hidden',
  },
  pickerItem: {
    fontSize: ms(14, 0.3),
    color: '#333',
  },
  swipeIndicator: {
    fontSize: ms(10, 0.3),
    color: '#999',
    paddingHorizontal: ms(5),
  },
  fileButton: {
    backgroundColor: '#FFF',
    borderRadius: ms(10),
    borderWidth: ms(1),
    borderColor: '#DDD',
    borderStyle: 'dashed',
    overflow: 'hidden',
  },
  uploadPlaceholder: {
    padding: ms(25),
    alignItems: 'center',
  },
  uploadIcon: {
    fontSize: ms(32, 0.3),
    marginBottom: ms(10),
  },
  fileButtonText: {
    fontSize: ms(14, 0.3),
    color: '#1C2C4F',
    textAlign: 'center',
  },
  fileHintText: {
    fontSize: ms(12, 0.3),
    color: '#999',
    marginTop: ms(5),
  },
  selectedFileContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: ms(10),
  },
  thumbnailImage: {
    width: s(60),
    height: s(60),
    borderRadius: ms(8),
    backgroundColor: '#f0f0f0',
  },
  fileDetails: {
    flex: 1,
    marginLeft: ms(12),
  },
  fileNameText: {
    fontSize: ms(14, 0.3),
    color: '#333',
    fontWeight: '500',
  },
  fileSizeText: {
    fontSize: ms(12, 0.3),
    color: '#666',
    marginTop: ms(2),
  },
  changeText: {
    fontSize: ms(14, 0.3),
    color: '#29BFC0',
    fontWeight: '600',
  },
  uploadButton: {
    backgroundColor: '#1C2C4F',
    borderRadius: ms(10),
    padding: ms(16),
    alignItems: 'center',
    marginBottom: ms(20),
  },
  uploadButtonDisabled: {
    opacity: 0.5,
  },
  uploadButtonText: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#FFFFFF',
  },
  infoContainer: {
    backgroundColor: '#E8F4F8',
    borderRadius: ms(10),
    padding: ms(15),
  },
  infoTitle: {
    fontSize: ms(14, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginBottom: ms(10),
  },
  infoText: {
    fontSize: ms(13, 0.3),
    color: '#1C2C4F',
    marginBottom: ms(6),
    paddingLeft: ms(5),
  },
  approvedContainer: {
    backgroundColor: '#D4EDDA',
    borderRadius: ms(10),
    padding: ms(20),
    marginBottom: ms(20),
  },
  approvedTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: 'bold',
    color: '#155724',
    marginBottom: ms(10),
  },
  approvedText: {
    fontSize: ms(14, 0.3),
    color: '#155724',
    marginBottom: ms(15),
  },
  secretCodeButton: {
    backgroundColor: '#28A745',
    borderRadius: ms(10),
    padding: ms(15),
    alignItems: 'center',
  },
  secretCodeButtonText: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#FFFFFF',
  },
  completedContainer: {
    backgroundColor: '#D4EDDA',
    borderRadius: ms(10),
    padding: ms(20),
    marginBottom: ms(20),
  },
  completedTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: 'bold',
    color: '#155724',
    marginBottom: ms(10),
  },
  completedText: {
    fontSize: ms(14, 0.3),
    color: '#155724',
  },
  statusContainer: {
    backgroundColor: '#FFF',
    borderRadius: ms(10),
    padding: ms(15),
    marginBottom: ms(20),
    borderWidth: ms(1),
    borderColor: '#DDD',
  },
  statusTitle: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginBottom: ms(10),
  },
  statusBadge: {
    alignSelf: 'flex-start',
    paddingHorizontal: ms(12),
    paddingVertical: ms(6),
    borderRadius: ms(15),
    marginBottom: ms(15),
  },
  statusBadgeText: {
    color: '#FFF',
    fontWeight: '600',
    fontSize: ms(13, 0.3),
  },
  scoreContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: ms(15),
    paddingBottom: ms(15),
    borderBottomWidth: ms(1),
    borderBottomColor: '#EEE',
  },
  scoreLabel: {
    fontSize: ms(14, 0.3),
    color: '#666',
  },
  scoreValue: {
    fontSize: ms(20, 0.3),
    fontWeight: 'bold',
    color: '#1C2C4F',
  },
  factorsContainer: {
    marginTop: ms(5),
  },
  factorsTitle: {
    fontSize: ms(13, 0.3),
    fontWeight: '600',
    color: '#666',
    marginBottom: ms(8),
  },
  factorRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: ms(6),
  },
  factorLabel: {
    fontSize: ms(13, 0.3),
    color: '#666',
  },
  factorValue: {
    fontSize: ms(13, 0.3),
    fontWeight: '600',
  },
});
